/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";

export default styled(Badge)(({ theme, ownerState }) => {
  const { palette, typography, borders, functions } = theme;
  const { color, circular, border, size, indicator, variant, container, children } = ownerState;

  const { gradients, badgeColors } = palette || {};
  const whiteMain = palette?.white?.main ?? "#ffffff";
  const darkMain = palette?.dark?.main ?? palette?.grey?.[900] ?? "#212121";
  const badgeSizeScale = typography.size || {};
  const { fontWeightBold } = typography;
  const { borderRadius, borderWidth } = borders;
  const { pxToRem, linearGradient } = functions;

  // padding values
  const paddings = {
    xs: "0.45em 0.775em",
    sm: "0.55em 0.9em",
    md: "0.65em 1em",
    lg: "0.85em 1.375em",
  };

  // fontSize: Material Dashboard adds typography.size; plain MUI themes omit it — fallback to variants.
  const fontSizeValue =
    size === "xs"
      ? badgeSizeScale.xxs ?? typography.caption?.fontSize ?? pxToRem(10.4)
      : badgeSizeScale.xs ?? typography.body2?.fontSize ?? pxToRem(12);

  // border value
  const borderValue = border ? `${borderWidth[3]} solid ${whiteMain}` : "none";

  // borderRadius value
  const borderRadiusValue = circular ? borderRadius.section : borderRadius.md;

  // styles for the badge with indicator={true}
  const indicatorStyles = (sizeProp) => {
    let widthValue = pxToRem(20);
    let heightValue = pxToRem(20);

    if (sizeProp === "medium") {
      widthValue = pxToRem(24);
      heightValue = pxToRem(24);
    } else if (sizeProp === "large") {
      widthValue = pxToRem(32);
      heightValue = pxToRem(32);
    }

    return {
      width: widthValue,
      height: heightValue,
      display: "grid",
      placeItems: "center",
      textAlign: "center",
      borderRadius: "50%",
      padding: 0,
      border: borderValue,
    };
  };

  const isHexColor = (c) => typeof c === "string" && /^#[0-9a-f]{3,8}$/i.test(c.trim());

  // styles for the badge with variant="gradient"
  const gradientStyles = (colorProp) => {
    const fallbackBg = palette?.primary?.main || palette?.info?.main || "#52009b";
    if (isHexColor(colorProp)) {
      return {
        background: colorProp.trim(),
        color: whiteMain,
      };
    }
    if (!gradients || typeof gradients !== "object") {
      return {
        background: fallbackBg,
        color: whiteMain,
      };
    }
    const gradEntry = gradients[colorProp] || gradients.info;
    if (!gradEntry || gradEntry.main == null || gradEntry.state == null) {
      return {
        background: fallbackBg,
        color: whiteMain,
      };
    }
    const backgroundValue = linearGradient(gradEntry.main, gradEntry.state);
    const colorValue = colorProp === "light" ? darkMain : whiteMain;

    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  // styles for the badge with variant="contained"
  const containedStyles = (colorProp) => {
    const fallbackBg = palette?.primary?.main || palette?.info?.main || "#52009b";
    if (isHexColor(colorProp)) {
      return {
        background: colorProp.trim(),
        color: whiteMain,
      };
    }
    if (!badgeColors || typeof badgeColors !== "object") {
      return {
        background: fallbackBg,
        color: whiteMain,
      };
    }
    const entry = badgeColors[colorProp] || badgeColors.info;
    const backgroundValue = entry?.background ?? fallbackBg;
    let colorValue = entry?.text ?? whiteMain;

    if (colorProp === "light") {
      colorValue = darkMain;
    }
    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  // styles for the badge with no children and container={false}
  const standAloneStyles = () => ({
    position: "static",
    marginLeft: pxToRem(8),
    transform: "none",
    fontSize: pxToRem(9),
  });

  // styles for the badge with container={true}
  const containerStyles = () => ({
    position: "relative",
    transform: "none",
  });

  return {
    "& .MuiBadge-badge": {
      height: "auto",
      padding: paddings[size] || paddings.xs,
      fontSize: fontSizeValue,
      fontWeight: fontWeightBold,
      textTransform: "uppercase",
      lineHeight: 1,
      textAlign: "center",
      whiteSpace: "nowrap",
      verticalAlign: "baseline",
      border: borderValue,
      borderRadius: borderRadiusValue,
      ...(indicator && indicatorStyles(size)),
      ...(variant === "gradient" && gradientStyles(color)),
      ...(variant === "contained" && containedStyles(color)),
      ...(!children && !container && standAloneStyles(color)),
      ...(container && containerStyles(color)),
    },
  };
});
